import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import {
  AppUser,
  AppRole,
  Permission,
  hasRole,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  createAppUserFromToken,
} from '../models/permissions.model';

/**
 * =======================================================================
 * AUTHORIZATION SERVICE
 * =======================================================================
 * 
 * Centralized service for managing user authorization across the app.
 * 
 * Security Principles:
 * - Single source of truth for current user
 * - Reactive signals for automatic UI updates
 * - Convenience methods for permission checks
 * - Clear separation between authentication and authorization
 * 
 * Usage:
 * ```typescript
 * // In component
 * constructor(private authz: AuthorizationService) {}
 * 
 * canEditSupplier = computed(() => 
 *   this.authz.hasPermission(Permission.EDIT_SUPPLIER)
 * );
 * 
 * // In template
 * @if (authz.hasPermission(Permission.CREATE_SUPPLIER)()) {
 *   <button>Create Supplier</button>
 * }
 * ```
 * 
 * IMPORTANT: This service handles UI-level authorization only.
 * Always validate permissions on the server before executing actions.
 * =======================================================================
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private router = inject(Router);
  private authService = inject(AuthService); // ← Usa AuthService per validare JWT

  /**
   * Current authenticated user
   * null = not authenticated
   */
  private currentUserState = signal<AppUser | null>(null);

  /**
   * Public read-only access to current user
   */
  readonly currentUser = this.currentUserState.asReadonly();

  /**
   * Computed signals for common checks
   */
  readonly isAuthenticated = computed(() => this.currentUser() !== null);
  readonly isAdmin = computed(() => hasRole(this.currentUser(), AppRole.SUPER_ADMIN));
  readonly isBackoffice = computed(() => {
    const user = this.currentUser();
    return hasRole(user, AppRole.SUPER_ADMIN) ||
           hasRole(user, AppRole.BACKOFFICE_ADMIN) ||
           hasRole(user, AppRole.BACKOFFICE_PROFESSIONAL) ||
           hasRole(user, AppRole.BACKOFFICE_SUPPLIER);
  });
  readonly isProfessional = computed(() => hasRole(this.currentUser(), AppRole.PROFESSIONAL));
  readonly isCollaborator = computed(() => hasRole(this.currentUser(), AppRole.COLLABORATOR));

  constructor() {
    // 🆕 Initialize immediately from current token (if exists)
    const existingUser = this.authService.getCurrentUser();
    if (existingUser) {
      const appUser = createAppUserFromToken(existingUser);
      this.currentUserState.set(appUser);
      console.log('⚡ [Authorization] User initialized from existing token:', appUser.name);
    }
    
    // Subscribe to AuthService's currentUser$ to auto-sync on changes
    this.authService.currentUser$.subscribe(userProfile => {
      if (userProfile) {
        const appUser = createAppUserFromToken(userProfile);
        this.currentUserState.set(appUser);
        this.saveToStorage(appUser);
        console.log('✅ [Authorization] User synced from AuthService:', appUser.name);
      } else {
        this.currentUserState.set(null);
        this.removeFromStorage();
        console.log('🚪 [Authorization] User cleared (logged out)');
      }
    });
  }

  // ==================== USER MANAGEMENT ====================

  /**
   * Set current user from JWT payload or AppUser object
   */
  setUser(userOrPayload: AppUser | any): void {
    const user = this.isAppUser(userOrPayload)
      ? userOrPayload
      : createAppUserFromToken(userOrPayload);

    this.currentUserState.set(user);
    this.saveToStorage(user);

    console.log('✅ [Authorization] User set:', {
      name: user.name,
      roles: user.roles,
      permissions: user.permissions.length + ' permissions',
    });
  }

  /**
   * Clear current user (logout)
   */
  clearUser(): void {
    this.currentUserState.set(null);
    this.removeFromStorage();
    console.log('🚪 [Authorization] User cleared');
  }

  /**
   * Get current user (or null)
   */
  getUser(): AppUser | null {
    return this.currentUser();
  }

  /**
   * Get current user or throw error
   */
  getUserOrThrow(): AppUser {
    const user = this.currentUser();
    if (!user) {
      throw new Error('No authenticated user');
    }
    return user;
  }

  // ==================== PERMISSION CHECKS ====================

  /**
   * Check if current user has specific role
   * Returns a computed signal for reactive UI
   */
  hasRole(role: AppRole) {
    return computed(() => hasRole(this.currentUser(), role));
  }

  /**
   * Check if current user has specific permission
   * Returns a computed signal for reactive UI
   */
  hasPermission(permission: Permission) {
    return computed(() => hasPermission(this.currentUser(), permission));
  }

  /**
   * Check if current user has ANY of the specified permissions
   * Returns a computed signal for reactive UI
   */
  hasAnyPermission(permissions: Permission[]) {
    return computed(() => hasAnyPermission(this.currentUser(), permissions));
  }

  /**
   * Check if current user has ALL of the specified permissions
   * Returns a computed signal for reactive UI
   */
  hasAllPermissions(permissions: Permission[]) {
    return computed(() => hasAllPermissions(this.currentUser(), permissions));
  }

  /**
   * Synchronous permission check (non-reactive)
   * Use when you need immediate boolean result
   */
  canDo(permission: Permission): boolean {
    return hasPermission(this.currentUser(), permission);
  }

  /**
   * Synchronous role check (non-reactive)
   */
  isRole(role: AppRole): boolean {
    return hasRole(this.currentUser(), role);
  }

  // ==================== NAVIGATION GUARDS ====================

  /**
   * Guard: Require authentication
   * Redirects to login if not authenticated
   */
  requireAuth(redirectUrl?: string): boolean {
    if (!this.isAuthenticated()) {
      console.warn('⚠️ [Authorization] Authentication required');
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: redirectUrl || this.router.url },
      });
      return false;
    }
    return true;
  }

  /**
   * Guard: Require specific permission
   * Redirects to unauthorized page if lacking permission
   */
  requirePermission(permission: Permission, redirectUrl = '/unauthorized'): boolean {
    if (!this.requireAuth()) {
      return false;
    }

    if (!this.canDo(permission)) {
      console.warn('⚠️ [Authorization] Permission denied:', permission);
      this.router.navigate([redirectUrl]);
      return false;
    }

    return true;
  }

  /**
   * Guard: Require specific role
   * Redirects to unauthorized page if lacking role
   */
  requireRole(role: AppRole, redirectUrl = '/unauthorized'): boolean {
    if (!this.requireAuth()) {
      return false;
    }

    if (!this.isRole(role)) {
      console.warn('⚠️ [Authorization] Role required:', role);
      this.router.navigate([redirectUrl]);
      return false;
    }

    return true;
  }

  /**
   * Guard: Require ANY of the specified permissions
   */
  requireAnyPermission(permissions: Permission[], redirectUrl = '/unauthorized'): boolean {
    if (!this.requireAuth()) {
      return false;
    }

    if (!hasAnyPermission(this.currentUser(), permissions)) {
      console.warn('⚠️ [Authorization] At least one permission required:', permissions);
      this.router.navigate([redirectUrl]);
      return false;
    }

    return true;
  }

  /**
   * Guard: Require ALL of the specified permissions
   */
  requireAllPermissions(permissions: Permission[], redirectUrl = '/unauthorized'): boolean {
    if (!this.requireAuth()) {
      return false;
    }

    if (!hasAllPermissions(this.currentUser(), permissions)) {
      console.warn('⚠️ [Authorization] All permissions required:', permissions);
      this.router.navigate([redirectUrl]);
      return false;
    }

    return true;
  }

  // ==================== PERSISTENCE ====================

  /**
   * Load user from AuthService JWT validation on service initialization
   * ✅ SECURE: Gets user from JWT token instead of localStorage
   */
  private loadFromAuthService(): void {
    try {
      // Get user from AuthService which re-parses JWT token
      const userProfile = this.authService.getCurrentUser();
      
      if (userProfile) {
        // Convert UserProfile to AppUser
        const appUser = createAppUserFromToken(userProfile);
        this.currentUserState.set(appUser);
        
        // Save to localStorage for UI performance (non-critical data)
        this.saveToStorage(appUser);
        
        console.log('✅ [Authorization] User loaded from JWT:', appUser.name);
      } else {
        console.log('⚠️ [Authorization] No valid JWT token found');
      }
    } catch (error) {
      console.error('❌ [Authorization] Failed to load user from JWT:', error);
      this.removeFromStorage();
    }
  }

  /**
   * Refresh user from JWT token (call after login or token refresh)
   * ✅ SECURE: Always validates from JWT
   */
  refreshUser(): void {
    this.loadFromAuthService();
  }

  /**
   * Load user from localStorage on service initialization
   * @deprecated This method is replaced by loadFromAuthService for security
   */
  private loadFromStorage(): void {
    try {
      // Migrate from localStorage to sessionStorage if needed
      const legacy = localStorage.getItem('app_user');
      if (legacy && !sessionStorage.getItem('app_user')) {
        sessionStorage.setItem('app_user', legacy);
        localStorage.removeItem('app_user');
      }
      const stored = sessionStorage.getItem('app_user');
      if (stored) {
        const user = JSON.parse(stored) as AppUser;

        // Check if token expired
        if (user.exp && user.exp < Math.floor(Date.now() / 1000)) {
          console.warn('⏰ [Authorization] Stored user token expired');
          this.removeFromStorage();
          return;
        }

        this.currentUserState.set(user);
        console.log('✅ [Authorization] User loaded from storage:', user.name);
      }
    } catch (error) {
      console.error('❌ [Authorization] Failed to load user from storage:', error);
      this.removeFromStorage();
    }
  }

  /**
   * Save user to sessionStorage.
   * SECURITY: Using sessionStorage (tab-scoped) instead of localStorage;
   * app_user contains role information which should not persist across sessions.
   */
  private saveToStorage(user: AppUser): void {
    try {
      sessionStorage.setItem('app_user', JSON.stringify(user));
      // Clean up legacy localStorage entry if present
      localStorage.removeItem('app_user');
    } catch (error) {
      console.error('❌ [Authorization] Failed to save user to storage:', error);
    }
  }

  /**
   * Remove user from sessionStorage.
   */
  private removeFromStorage(): void {
    sessionStorage.removeItem('app_user');
    localStorage.removeItem('app_user'); // legacy cleanup
  }

  /**
   * Type guard to check if object is AppUser
   */
  private isAppUser(obj: any): obj is AppUser {
    return obj && 
           typeof obj.id === 'string' &&
           typeof obj.email === 'string' &&
           Array.isArray(obj.roles) &&
           Array.isArray(obj.permissions);
  }
}
